import React, { Fragment, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { requestCommentInsert, requestCommentUpdate } from 'api/board/qna';
import Editor from 'components/common-sfims/form/Editor';
import Modal from 'components/common-sfims/modal/Common';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';

/**
 * 협업 게시판 > 질문 및 답변 - 답변 등록/수정
 */
const Comment = ({ show, inform, seq, onHide, onSetting }) => {
  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // 답변 등록/수정 요청 데이터
  const [commentData, setCommentData] = useObjReducer({});
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // 에디터 표시 여부
  const [isShowEditor, setIsShowEditor] = useState(false);

  /* watch */
  useEffect(() => {
    // 모달 표시 여부에 따라 에디터 표시/숨김
    setIsShowEditor(show);

    // 모달이 표시된 경우 데이터 초기화
    if (show) {
      setCommentData({ comment: inform.comment ?? '' });
    }
  }, [show]);

  /* event */
  // 설정
  const onSubmit = async () => {
    if (isDisableSetting) {
      return;
    }

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(commentData)) {
        throw {
          code: RESPONSE_CODE[
            inform.seq ? 'BadRequestUpdate' : 'BadRequestInsert'
          ]
        };
      }

      if (inform.seq) {
        const { code, message } = await requestCommentUpdate(
          inform.seq,
          commentData
        );

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      } else {
        const { code, message } = await requestCommentInsert(seq, commentData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      }

      // 설정 시 이벤트 실행
      onSetting();

      // 모달 숨김
      onHide();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{
        body: 'bg-light max-vh-75 scrollbar',
        footer: 'align-items-center justify-content-center'
      }}
      size="xl"
      onHide={onHide}
    >
      <Fragment key="header">
        {getText(inform.seq ? 'updateComment' : 'insertComment')}
      </Fragment>
      <Fragment key="body">
        <Form.Group>
          <Form.Label>* {getText('answer')}</Form.Label>
          {isShowEditor && (
            <Editor
              value={commentData.comment}
              onUpdate={(comment = '') => setCommentData({ comment })}
            />
          )}
        </Form.Group>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('cancel')}
        </Button>
        <Button disabled={isDisableSetting} className="ms-2" onClick={onSubmit}>
          {getText('ok')}
        </Button>
      </Fragment>
    </Modal>
  );
};

Comment.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 답변 정보
  inform: PropTypes.object,

  // 질문 번호
  seq: PropTypes.number,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Comment.defaultProps = {
  show: false,
  inform: {}
};

export default Comment;
